import { CacheEntry } from "../../Orchestrator/Types/Types/CacheType";
import { decodeData, encodeData } from "../Utils/general";
import { TTL } from "../Utils/TTL";

// How long past expiry an entry may still be served as STALE content
// (stale-while-revalidate). Beyond this, treat it as a genuine cache miss.
const STALE_GRACE_MS = TTL.DAY;

export class CacheManager {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private persistentKeys: Set<string> = new Set();
  // Bumping the suffix busts every persisted entry once, on the next deploy.
  // _v2: /places/all moved from a day-long persisted cache to a session-scoped
  // one; without the bust, returning visitors keep serving the old localStorage
  // entry (absolute expiry, up to 24h out) and still miss newly added places.
  // Cost is one cold CMS load per user, recovered on the same page view.
  private storageKey = 'CacheManagerCache_v2';
  // In-flight request coalescing: the cache only stores COMPLETED responses,
  // so concurrent identical requests fired before the first response lands
  // would each hit the network. Callers register the fetch promise here so
  // followers await the same promise instead.
  private inflight: Map<string, Promise<any>> = new Map();

  constructor(
    private defaultSlidingTTL: number = TTL.MIN_5, // 5 minutes
    private defaultClassicTTL: number = TTL.MIN_10  // 10 minutes    
  ) {
    this.hydrateFromStorage();
  }

  async getOrFetch<T>(
    url: string,
    mode: "classic" | "sliding" = "classic",
    customTTL?: number,
    customExpiry?: number,
    body?: any,
    method: string = 'GET',
    persist: boolean = false,
    headers: Record<string, string> = {}
  ): Promise<T | null> {
    try {
      const key = this.getCacheKey(url, body, method, headers);
      const cachedEntry = this.cache.get(key);

      if (cachedEntry && this.isCacheValid(cachedEntry)) {
        if (mode === "sliding") {
          this.updateExpiry(cachedEntry, customTTL, customExpiry);
          if (persist && this.persistentKeys.has(key)) {
            this.syncToStorage();
          }
        }

        let data = decodeData<T>(cachedEntry.data);
        if (!data) {
          this.cache.delete(key);
          this.persistentKeys.delete(key);
          return null;
        }

        return data;
      }
      return null;
    } catch (error) {
      console.error('Error in getOrFetch:', error);
      return null;
    }
  }

  /**
   * Returns EXPIRED-but-within-grace data for stale-while-revalidate, or null
   * when the entry is missing, still fresh (use getOrFetch), or too old.
   */
  getStale<T>(
    url: string,
    body?: any,
    method: string = 'GET',
    headers: Record<string, string> = {}
  ): T | null {
    try {
      const key = this.getCacheKey(url, body, method, headers);
      const entry = this.cache.get(key);
      if (!entry || this.isCacheValid(entry) || !this.isWithinStaleGrace(entry)) return null;
      return decodeData<T>(entry.data);
    } catch {
      return null;
    }
  }

  set<T>(
    url: string,
    data: T,
    mode: "classic" | "sliding",
    customTTL?: number,
    customExpiry?: number,
    body?: any,
    method: string = 'GET',
    persist: boolean = false,
    headers: Record<string, string> = {}
  ): void {
    try {
      const key = this.getCacheKey(url, body, method, headers);
      const expiry = this.calculateExpiry(customTTL, customExpiry, mode);

      try {
        const encodedData = encodeData(data);
        this.cache.set(key, { url, method, data: encodedData, expiry });
      } catch (encodeError) {
        console.error('Failed to encode data for caching:', encodeError);
        return;
      }

      if (persist) {
        this.persistentKeys.add(key);
        this.syncToStorage();
      } else {
        this.persistentKeys.delete(key);
      }
    } catch (error) {
      console.error('Error in set:', error);
    }
  }

  /**
   * Runs `fetcher` unless an identical request (same cache key) is already in
   * flight, in which case the existing promise is joined. Only use for
   * requests that are safe to share (cached GET/POST reads — never mutations).
   *
   * Notes:
   * - Followers receive a structured CLONE of the result: cache hits already
   *   decode a fresh copy per caller, and Actions in this codebase mutate
   *   responses in place — a shared reference would let one consumer corrupt
   *   another's data.
   * - The key deliberately omits auth state (matching the response cache), so
   *   requests fired across a login transition within the in-flight window
   *   share one response — consistent with existing cache behavior.
   */
  coalesce<T>(
    url: string,
    body: any,
    method: string,
    headers: Record<string, string>,
    fetcher: () => Promise<T>
  ): Promise<T> {
    const key = this.getCacheKey(url, body, method, headers);
    const existing = this.inflight.get(key);
    if (existing) {
      return (existing as Promise<T>).then((result) => structuredClone(result));
    }

    const promise = fetcher().finally(() => {
      this.inflight.delete(key);
    });
    this.inflight.set(key, promise);
    return promise;
  }

  invalidate(url: string, body?: any, method: string = 'GET'): void {
    const key = this.getCacheKey(url, body, method);
    this.cache.delete(key);
    this.persistentKeys.delete(key);
    // Removals must reach localStorage synchronously — a debounced write that
    // never fires (e.g. navigation right after) would resurrect the entry on
    // the next session's hydrateFromStorage().
    this.flushToStorage();
  }

  clearAll(): void {
    this.cache.clear();
    this.persistentKeys.clear();
    // Called on logout (SignOutAction/useAuth) right before a redirect to
    // Keycloak — the purge MUST hit localStorage before the page unloads.
    this.flushToStorage();
    console.info("[CacheManager] Clear all cache.");
  }

  getExpiry(url: string, body?: any, method: string = 'GET'): number | null {
    const key = this.getCacheKey(url, body, method);
    const cachedEntry = this.cache.get(key);
    return cachedEntry ? cachedEntry.expiry - Date.now() : null;
  }

  exists(url: string, body?: any, method: string = 'GET'): boolean {
    const key = this.getCacheKey(url, body, method);
    return this.cache.has(key);
  }

  getAll(): Map<string, CacheEntry<any>> {
    return new Map(this.cache);
  }

  async getMultipleOrFetch<T>(
    urls: string[],
    mode: "classic" | "sliding" = "classic",
    customTTL?: number,
    customExpiry?: number,
    bodies?: any[],
    methods?: string[],
    persist?: boolean[]
  ): Promise<(T | null)[]> {
    return Promise.all(
      urls.map((url, i) => this.getOrFetch<T>(
        url,
        mode,
        customTTL,
        customExpiry,
        bodies ? bodies[i] : undefined,
        methods ? methods[i] : 'GET',
        persist ? persist[i] : true
      ))
    );
  }

  update<T>(url: string, data: T, body?: any, method: string = 'GET'): void {
    try {
      const key = this.getCacheKey(url, body, method);
      const cachedEntry = this.cache.get(key);

      if (cachedEntry) {
        try {
          cachedEntry.data = encodeData(data);
          this.updateExpiry(cachedEntry);

          if (this.persistentKeys.has(key)) {
            this.syncToStorage();
          }
        } catch (encodeError) {
          console.error('Failed to encode data for update:', encodeError);
          // Remove the corrupted entry
          this.cache.delete(key);
          this.persistentKeys.delete(key);
        }
      }
    } catch (error) {
      console.error('Error in update:', error);
    }
  }

  // --- Cache key helper ---
  private getCacheKey(url: string, body?: any, method: string = 'GET', headers?: Record<string, string>): string {
    let base = method.toUpperCase() + '::' + url;
    if (body) {
      try {
        base += '::' + this.stableStringify(body);
      } catch {
        base += '::' + String(body);
      }
    }
    if (headers) {
      try {
        base += '::' + this.stableStringify(headers);
      } catch {
        base += '::' + String(headers);
      }
    }
    return this.djb2Hash(base);
  }

  // Stable, deep, sorted stringify for objects/arrays
  private stableStringify(obj: any): string {
    if (obj === null || typeof obj !== 'object') return JSON.stringify(obj);
    if (Array.isArray(obj)) {
      return '[' + obj.map((item) => this.stableStringify(item)).join(',') + ']';
    }
    // Object: sort keys
    const keys = Object.keys(obj).sort();
    return '{' + keys.map(k => JSON.stringify(k) + ':' + this.stableStringify(obj[k])).join(',') + '}';
  }

  // Simple djb2 hash function for strings
  private djb2Hash(str: string): string {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) + str.charCodeAt(i); // hash * 33 + c
      hash = hash & 0xFFFFFFFF; // force 32bit
    }
    return 'cache_' + (hash >>> 0).toString(16);
  }

  // --- Persistence helpers ---
  private syncTimer: ReturnType<typeof setTimeout> | null = null;

  // Writing re-serializes the WHOLE persistent map, so bursts of set() calls
  // (e.g. ~35 CMS endpoints at cold start) are coalesced into one trailing
  // write instead of O(n) full-map serializations. The map itself is always
  // current — only the localStorage mirror lags by up to 300ms.
  private syncToStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (this.syncTimer !== null) return;
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      this.writeToStorage();
    }, 300);
  }

  // Synchronous write for cache removals (logout purge, invalidation): cancel
  // any pending debounced write and persist the current map immediately.
  private flushToStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (this.syncTimer !== null) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    this.writeToStorage();
  }

  private writeToStorage(): void {
    try {
      const obj: Record<string, CacheEntry<any>> = {};
      for (const [key, entry] of Array.from(this.cache.entries())) {
        // Persist entries that are marked persistent and either still fresh or
        // within the stale grace window (kept for stale-while-revalidate).
        if (this.persistentKeys.has(key) && this.isWithinStaleGrace(entry)) {
          obj[key] = entry;
        }
      }
      localStorage.setItem(this.storageKey, JSON.stringify(obj));
    } catch (e) {
      // Ignore storage errors
    }
  }

  private hydrateFromStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;

      const obj = JSON.parse(raw) as Record<string, CacheEntry<any>>;
      const now = Date.now();

      for (const [key, entry] of Object.entries(obj)) {
        try {
          // Keep expired-but-within-grace entries so a new session can render
          // stale content instantly while revalidating (stale-while-revalidate).
          if (entry.expiry + STALE_GRACE_MS > now) {
            // Validate that the data can be decoded before adding to cache
            if (typeof entry.data === 'string') {
              atob(entry.data); // Test decode - will throw if invalid
              this.cache.set(key, entry);
              this.persistentKeys.add(key);
            } else {
              console.warn('Invalid cache entry data format for key:', key);
            }
          }
        } catch (decodeError) {
          console.error('Failed to validate cached entry for key:', key, decodeError);
          // Skip corrupted entries
        }
      }
    } catch (error) {
      console.error('Error hydrating from storage:', error);
      // Clear corrupted storage
      try {
        localStorage.removeItem(this.storageKey);
      } catch (clearError) {
        console.error('Failed to clear corrupted storage:', clearError);
      }
    }
  }

  private isCacheValid<T>(cachedEntry: CacheEntry<T>): boolean {
    return Date.now() < cachedEntry.expiry;
  }

  private isWithinStaleGrace<T>(cachedEntry: CacheEntry<T>): boolean {
    return Date.now() < cachedEntry.expiry + STALE_GRACE_MS;
  }

  private updateExpiry<T>(
    cachedEntry: CacheEntry<T>,
    customTTL?: number,
    customExpiry?: number
  ): void {
    cachedEntry.expiry = this.calculateExpiry(
      customTTL,
      customExpiry,
      "sliding"
    );
  }

  private calculateExpiry(
    customTTL?: number,
    customExpiry?: number,
    mode?: "classic" | "sliding"
  ): number {
    return customExpiry ?? Date.now() + (customTTL ?? (mode === "sliding" ? this.defaultSlidingTTL : this.defaultClassicTTL));
  }
}

export const cacheManager = new CacheManager();
